import { useMemo, useState } from "react";
import PropertyCard from "../components/PropertyCard";
import { PROPERTIES, type Property } from "../data/properties";

type StatusFilter = "All" | Property["status"];

export default function PropertiesPage(): JSX.Element {
    const [query, setQuery] = useState<string>("");
    const [status, setStatus] = useState<StatusFilter>("All");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();

        return PROPERTIES.filter((p) => {
            const matchesQuery =
                !q ||
                p.title.toLowerCase().includes(q) ||
                p.location.toLowerCase().includes(q) ||
                p.tags.some((t) => t.toLowerCase().includes(q));

            const matchesStatus = status === "All" ? true : p.status === status;

            return matchesQuery && matchesStatus;
        });
    }, [query, status]);

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h1 className="text-2xl font-semibold tracking-tight">
                        Properties
                    </h1>
                    <div className="text-sm text-[rgb(var(--muted))]">
                        Search and tap a card to view details.
                    </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 md:w-[28rem]">
                    <label className="space-y-1">
                        <div className="text-xs font-medium text-[rgb(var(--muted2))]">
                            Search
                        </div>
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Wallowa, river, workshop..."
                            className="w-full rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.50)] px-4 py-3 text-sm text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted3))] outline-none transition focus:border-[rgb(var(--border)/0.20)] focus:ring-2 focus:ring-[rgb(var(--accent)/0.18)]"
                        />
                    </label>

                    <label className="space-y-1">
                        <div className="text-xs font-medium text-[rgb(var(--muted2))]">
                            Status
                        </div>
                        <select
                            value={status}
                            onChange={(e) =>
                                setStatus(e.target.value as StatusFilter)
                            }
                            className="w-full cursor-pointer rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.50)] px-4 py-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:border-[rgb(var(--border)/0.20)] focus:ring-2 focus:ring-[rgb(var(--accent)/0.18)]"
                        >
                            <option value="All">All</option>
                            <option value="For Sale">For Sale</option>
                            <option value="Pending">Pending</option>
                            <option value="Sold">Sold</option>
                        </select>
                    </label>
                </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((p) => (
                    <PropertyCard key={p.shortId} property={p} />
                ))}
            </div>

            {filtered.length === 0 ? (
                <div className="rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--surface)/0.05)] p-6 text-sm text-[rgb(var(--muted2))]">
                    No properties match that search.
                </div>
            ) : null}
        </div>
    );
}
